import { SignalRHub } from '../utils/signalRNotifications/signalR-hub'
import React, { createContext, useEffect } from 'react'

import { useSelector } from 'react-redux'
import {
	NotificationMessage,
	SignalREvents,
	SIgnalRNotificationEventType,
} from '../model/signalRNotifications'
import { notificationsService } from '../utils/signalRNotifications/notifications-service'
import { useEvent } from '../hooks/useEvent'
import { selectAuthToken } from '../features/auth/selectors'
import { selectStoreId } from '../features/settings/selectors'

interface SignalRContextProps {
	connectionId: string | null
}

export interface SignalRProviderProps {
	children?: React.ReactNode
}

export const SignalRContext = createContext<SignalRContextProps>({
	connectionId: null,
})

/**
 * `SignalRProvider` is a React functional component that provides a context for managing
 * SignalR connections and handling real-time notifications. It establishes a SignalR connection,
 * listens for events, and emits them as application-level events using the gemini event emitter.
 * Rather than accessing the context directly, it's recommended to use the useNotificationsStore hook
 * to retrieve the connection state. This approach ensures compatibility across microfrontends,
 * unlike the context which is limited to the current React tree.
 *
 * @returns {JSX.Element} A context provider that supplies the current SignalR connection ID.
 *
 * @remarks
 * - This provider listens for the following SignalR events:
 *   - `AppEvent`: Transforms a SignalR message into an application-level event.
 *   - `SignalRConnected`: Handles the connection establishment and emits a `SignalRConnected` event.
 *   - `SignalRDisconnected`: Handles the disconnection and emits a `SignalRDisconnected` event.
 * - On cleanup, it removes SignalR event listeners and disconnects the SignalR connection if active.
 */
export const SignalRProvider: React.FC<SignalRProviderProps> = ({
	children,
}) => {
	const [connectionId, setConnectionId] = React.useState<string | null>(
		SignalRHub.getConnectionId(),
	)
	const authToken = useSelector(selectAuthToken)
	const storeId = useSelector(selectStoreId)

	const { emit } = useEvent()

	// This ensures the object reference stays stable unless connectionId changes, resolving the sonarjs/jsx-no-constructed-context-values warning.
	const contextValue = React.useMemo(() => ({ connectionId }), [connectionId])

	function onMessageReceived(message: NotificationMessage) {
		// Transform a SignalR message into an event
		// eslint-disable-next-line no-console
		console.info('[SignalRProvider] Message Received.', message)
		emit<NotificationMessage>(message.type, message)
	}

	function onSignalRConnected(newConnectionId: string) {
		// eslint-disable-next-line no-console
		console.log('[SignalRProvider] SignalR Connected', newConnectionId)
		notificationsService.setConnectionId(newConnectionId)
		setConnectionId(newConnectionId)
		// Emit a SignalRConnected event
		emit<NotificationMessage>(SIgnalRNotificationEventType.SignalRConnected, {
			type: SIgnalRNotificationEventType.SignalRConnected,
			data: { newConnectionId },
		} as NotificationMessage)

		const groups = notificationsService.groups
		if (groups.length > 0) {
			// eslint-disable-next-line no-console
			console.log('[SignalRProvider] Rejoining groups', groups.join(','))
			// Rejoining connection to groups
			groups.forEach(group => notificationsService.addConnectionToGroup(group))
		}
	}

	function onSignalRDisconnected() {
		// eslint-disable-next-line no-console
		console.log('[SignalRProvider] SignalR Disconnected')

		notificationsService.setConnectionId(null)
		// Emit a SignalRDisconnected event
		emit<NotificationMessage>(
			SIgnalRNotificationEventType.SignalRDisconnected,
			{
				type: SIgnalRNotificationEventType.SignalRDisconnected,
				data: { connectionId },
			} as NotificationMessage,
		)
	}

	// useEffectOnce instead of useEffect with no dependencies to avoid duplicate execution on development mode with StrictMode
	useEffect(() => {
		notificationsService.setConnectionId(null)
		if (!authToken || !storeId) {
			return
		}
		SignalRHub.on(SignalREvents.AppEvent, onMessageReceived)
		SignalRHub.on(SignalREvents.SignalRConnected, onSignalRConnected)
		SignalRHub.on(SignalREvents.SignalRDisconnected, onSignalRDisconnected)
		SignalRHub.signalRConnectAsync()

		return () => {
			// SignalRDispatcher useEffect cleanup
			SignalRHub.off(SignalREvents.AppEvent)
			SignalRHub.signalRDisconnectAsync()
		}
	}, [authToken, storeId])

	return (
		<SignalRContext.Provider value={contextValue}>
			{children}
		</SignalRContext.Provider>
	)
}
